import { useEffect, useRef } from "react";

interface MembershipRow {
  removed_at?: number | null;
}

interface MembershipGuardConfig {
  selectedWorkspace: string | null;
  currentUserWsMembership: MembershipRow | null | undefined;
  myWsMembershipsStatus: { type: string };
  currentWorkspace: { name?: string } | null;
  onMembershipLost?: (workspaceName: string) => void;
}

/**
 * Detects membership loss (kicked/removed from workspace) and triggers
 * callback. Two transitions trigger the "you've been kicked" modal:
 *
 *   1. **Soft-delete** — the membership row stays visible to the kicked
 *      user via `workspace_members.byUser` (which intentionally does
 *      NOT filter `removed_at` so kicked users can detect their own
 *      tombstone), but `removed_at` flips from null to a timestamp.
 *      Fires immediately — no rehome / reconnect needed.
 *
 *   2. **Row disappears** — the membership row leaves the kicked
 *      user's subscription entirely. This happens after Zero's
 *      permission-rehome reconnect (the kicked user no longer has
 *      auth to read their own tombstone, so it's dropped). Fires as
 *      a fallback in case (1) didn't run.
 *
 * Pre-PR #1756 only (2) was implemented, so the kicked user had to wait
 * for Zero's slow rehome — symptom: workspace still visible + partial
 * channels until reload. (1) is the eager detection path.
 *
 * Reset on workspace switch to avoid stale state from the previous
 * workspace. Only trigger when query status is "complete" — ignores
 * Zero re-sync transients.
 */
export function useSidebarMembershipLossDetection({
  selectedWorkspace,
  currentUserWsMembership,
  myWsMembershipsStatus,
  currentWorkspace,
  onMembershipLost,
}: MembershipGuardConfig) {
  const hadActiveMembershipRef = useRef(false);
  const prevWorkspaceRef = useRef(selectedWorkspace);

  useEffect(() => {
    if (prevWorkspaceRef.current !== selectedWorkspace) {
      hadActiveMembershipRef.current = false;
      prevWorkspaceRef.current = selectedWorkspace;
    }
    if (!selectedWorkspace) return;

    const isActiveMember =
      !!currentUserWsMembership && !currentUserWsMembership.removed_at;

    if (isActiveMember) {
      hadActiveMembershipRef.current = true;
      return;
    }

    // Either the row was soft-deleted (still present, removed_at set)
    // OR it disappeared entirely (post-rehome). Both signal kick.
    if (
      hadActiveMembershipRef.current &&
      myWsMembershipsStatus.type === "complete"
    ) {
      hadActiveMembershipRef.current = false;
      const wsName = currentWorkspace?.name || "this workspace";
      onMembershipLost?.(wsName);
    }
  }, [
    selectedWorkspace,
    currentUserWsMembership,
    myWsMembershipsStatus,
    currentWorkspace,
    onMembershipLost,
  ]);
}
